import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NotificationBell from './NotificationBell';
import * as listCache from '../../services/listCache';

const TopHeader = ({ onToggleSidebar, theme, onToggleTheme, onOpenPalette }) => {
  const navigate = useNavigate();
  const username = localStorage.getItem('username') || 'User';
  const isSuperAdmin = localStorage.getItem('is_superadmin') === 'true';

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!userMenuOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') { setUserMenuOpen(false); return; }
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', handler);
    };
  }, [userMenuOpen]);

  const handleSignOut = () => {
    ['customer_management_token', 'token', 'username', 'is_superadmin'].forEach(k => localStorage.removeItem(k));
    listCache.invalidateAll(); // don't let the next user in this tab see cached list data
    window.location.href = '/login';
  };

  return (
    <header className="top-header">
      {/* ── Left ── */}
      <div className="top-header-left">
        <button
          onClick={onToggleSidebar}
          className="top-header-icon-btn"
          type="button"
          aria-label="Toggle navigation"
        >
          <div className="menu-icon">
            <span className="menu-bar" />
            <span className="menu-bar" />
            <span className="menu-bar" />
          </div>
        </button>

        <div
          className="top-header-search"
          onClick={() => onOpenPalette?.('')}
          style={{ cursor: 'pointer' }}
          role="button"
          aria-label="Open command palette"
        >
          <span className="top-header-search-icon">🔍</span>
          <input
            className="top-header-search-input"
            type="text"
            placeholder="Search or type command…"
            readOnly
            style={{ cursor: 'pointer', pointerEvents: 'none' }}
          />
          <span className="top-header-shortcut">⌘K</span>
        </div>
      </div>

      {/* ── Right ── */}
      <div className="top-header-right">
        {/* Theme toggle */}
        <button
          className="top-header-icon-btn"
          type="button"
          onClick={onToggleTheme}
          aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
          title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
        >
          {theme === 'light' ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="5"/>
              <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
              <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
            </svg>
          )}
        </button>

        <NotificationBell />

        {/* User menu */}
        <div className="top-header-user-wrap" ref={wrapRef}>
          <button
            className="top-header-user"
            type="button"
            onClick={() => setUserMenuOpen(o => !o)}
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
          >
            <div className="top-header-avatar">
              {username.charAt(0).toUpperCase()}
            </div>
            <span className="top-header-username">{username}</span>
            <span className={`top-header-caret${userMenuOpen ? ' is-open' : ''}`}>⌄</span>
          </button>

          {userMenuOpen && (
            <div className="user-menu" role="menu">
              <div className="user-menu-identity">
                <div className="user-menu-identity-avatar">
                  {username.charAt(0).toUpperCase()}
                </div>
                <div className="user-menu-identity-info">
                  <span className="user-menu-identity-name">{username}</span>
                  <span className="user-menu-identity-role">
                    {isSuperAdmin ? 'Super Admin' : 'Staff'}
                  </span>
                </div>
              </div>

              <div className="user-menu-divider" />

              <button
                className="user-menu-item"
                type="button"
                role="menuitem"
                onClick={() => { setUserMenuOpen(false); navigate('/profile'); }}
              >
                <span className="user-menu-item-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                </span>
                <span className="user-menu-item-label">Profile</span>
              </button>

              <div className="user-menu-divider" />

              <button
                className="user-menu-item user-menu-item--danger"
                type="button"
                role="menuitem"
                onClick={handleSignOut}
              >
                <span className="user-menu-item-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                </span>
                <span className="user-menu-item-label">Sign Out</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default TopHeader;
